import { apiClient } from '@/utils/api'

export type RegistrationStatus = 'pending' | 'confirmed' | 'rejected'

export interface Registration {
  registrationId: string
  seasonId: string
  leagueId: string
  groupId: string
  userId: string
  profileId: string
  preferredBracketId: string
  assignedBracketId?: string
  status: RegistrationStatus
  createdAt: string
  updatedAt: string
}

export interface RegistrationWithProfile extends Registration {
  profile: {
    profileId: string
    displayName: string
    picture?: string
  }
  preferredBracketName?: string
  assignedBracketName?: string
}

interface RegistrationResponse {
  data: Registration
}

interface RegistrationsResponse {
  data: RegistrationWithProfile[]
  meta: {
    total: number
  }
}

interface PendingCountResponse {
  data: {
    count: number
  }
}

export const registrationService = {
  async register(
    leagueId: string,
    seasonId: string,
    preferredBracketId: string
  ): Promise<Registration> {
    const response = await apiClient.authPost<RegistrationResponse>(
      `/leagues/${leagueId}/seasons/${seasonId}/registrations`,
      { preferredBracketId }
    )
    return response.data
  },

  async getMyRegistration(
    leagueId: string,
    seasonId: string
  ): Promise<Registration | null> {
    const response = await apiClient.authGet<{ data: Registration | null }>(
      `/leagues/${leagueId}/seasons/${seasonId}/registrations/me`
    )
    return response.data
  },

  async list(
    leagueId: string,
    seasonId: string
  ): Promise<RegistrationWithProfile[]> {
    const response = await apiClient.authGet<RegistrationsResponse>(
      `/leagues/${leagueId}/seasons/${seasonId}/registrations`
    )
    return response.data
  },

  async getPendingCount(
    leagueId: string,
    seasonId: string
  ): Promise<number> {
    const response = await apiClient.authGet<PendingCountResponse>(
      `/leagues/${leagueId}/seasons/${seasonId}/registrations/pending-count`
    )
    return response.data.count
  },

  async confirm(
    leagueId: string,
    seasonId: string,
    registrationId: string,
    assignedBracketId: string
  ): Promise<Registration> {
    const response = await apiClient.authPost<RegistrationResponse>(
      `/leagues/${leagueId}/seasons/${seasonId}/registrations/${registrationId}/confirm`,
      { assignedBracketId }
    )
    return response.data
  },

  async reject(
    leagueId: string,
    seasonId: string,
    registrationId: string
  ): Promise<Registration> {
    const response = await apiClient.authPost<RegistrationResponse>(
      `/leagues/${leagueId}/seasons/${seasonId}/registrations/${registrationId}/reject`,
      {}
    )
    return response.data
  },

  async cancel(
    leagueId: string,
    seasonId: string,
    registrationId: string
  ): Promise<void> {
    await apiClient.authDelete(
      `/leagues/${leagueId}/seasons/${seasonId}/registrations/${registrationId}`
    )
  },
}
